import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import formService from '../../services/formService';
import projectService from '../../services/projectService';
import DatePickerField from '../../components/common/DatePickerField';
import '../monitoring/Monitoring.css';

const SEVERITY_OPTIONS = ['critical', 'major', 'minor'];

const PromoteModal = ({ submission, needsProject, projects, onClose, onSave, saving }) => {
  const [projectId, setProjectId] = useState('');
  const [title, setTitle] = useState('');
  const [severity, setSeverity] = useState('major');
  const [correctiveAction, setCorrectiveAction] = useState('');
  const [deadline, setDeadline] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (needsProject && !projectId) {
      toast.error('Please select a project for this finding');
      return;
    }
    onSave({
      ...(projectId && { projectId }),
      ...(title.trim() && { title: title.trim() }),
      severity,
      ...(correctiveAction.trim() && { correctiveAction: correctiveAction.trim() }),
      ...(deadline && { deadline }),
    });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="monitoring-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2><i className="fas fa-arrow-up"></i> Promote to Compliance Finding</h2>
          <button className="close-btn" onClick={onClose}><i className="fas fa-times"></i></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-content">
            <div className="form-grid">
              {needsProject && (
                <div className="form-group full-width">
                  <label>Project *</label>
                  <select value={projectId} onChange={(e) => setProjectId(e.target.value)} required>
                    <option value="">Select a project...</option>
                    {projects.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
                  </select>
                </div>
              )}
              <div className="form-group full-width">
                <label>Finding Title</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  placeholder="Leave blank to auto-generate from the form title"
                />
              </div>
              <div className="form-group">
                <label>Severity *</label>
                <select value={severity} onChange={(e) => setSeverity(e.target.value)} required>
                  {SEVERITY_OPTIONS.map((s) => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label>Deadline</label>
                <DatePickerField value={deadline} onChange={setDeadline} />
              </div>
              <div className="form-group full-width">
                <label>Corrective Action</label>
                <textarea
                  rows="3"
                  value={correctiveAction}
                  onChange={(e) => setCorrectiveAction(e.target.value)}
                  placeholder="What needs to be done?"
                />
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn-cancel" onClick={onClose} disabled={saving}>Cancel</button>
            <button type="submit" className="btn-primary-green" disabled={saving}>
              {saving ? 'Promoting...' : 'Promote'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

const FormSubmissions = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [form, setForm] = useState(null);
  const [submissions, setSubmissions] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notesBySubmission, setNotesBySubmission] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [promotingSubmission, setPromotingSubmission] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const data = await formService.getSubmissions(id);
      setForm(data.form);
      setSubmissions(data.submissions || []);
      setNotesBySubmission(Object.fromEntries((data.submissions || []).map((s) => [s.id, s.reviewNotes || ''])));
    } catch (error) {
      console.error('Failed to load submissions:', error);
      toast.error(error.response?.data?.error || 'Failed to load submissions');
      navigate('/forms');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => {
    (async () => {
      try {
        const data = await projectService.getAll();
        setProjects(data.projects || []);
      } catch (error) {
        console.error('Failed to fetch projects:', error);
      }
    })();
  }, []);

  const handleSaveNotes = async (submission) => {
    try {
      setSavingId(submission.id);
      await formService.updateSubmission(id, submission.id, {
        status: 'reviewed',
        reviewNotes: notesBySubmission[submission.id] || '',
      });
      toast.success('Marked as reviewed');
      fetchData();
    } catch (error) {
      console.error('Failed to update submission:', error);
      toast.error(error.response?.data?.error || 'Failed to update submission');
    } finally {
      setSavingId(null);
    }
  };

  const handlePromote = async (payload) => {
    try {
      setSavingId(promotingSubmission.id);
      await formService.promoteSubmission(id, promotingSubmission.id, payload);
      toast.success('Submission promoted to a compliance finding');
      setPromotingSubmission(null);
      fetchData();
    } catch (error) {
      console.error('Failed to promote submission:', error);
      toast.error(error.response?.data?.error || 'Failed to promote submission');
    } finally {
      setSavingId(null);
    }
  };

  if (loading) {
    return (
      <div className="monitoring-page">
        <div className="empty-state">
          <i className="fas fa-spinner fa-spin"></i>
          <h3>Loading submissions...</h3>
        </div>
      </div>
    );
  }

  if (!form) return null;

  return (
    <div className="monitoring-page">
      <div className="monitoring-header">
        <div>
          <Link to="/forms" style={{ color: '#6b7280', fontSize: '0.85rem', textDecoration: 'none' }}>
            <i className="fas fa-arrow-left"></i> Back to Forms
          </Link>
          <h1 style={{ marginTop: '0.5rem' }}><i className="fas fa-inbox"></i> {form.title}</h1>
          <p className="header-subtitle">{submissions.length} submission{submissions.length === 1 ? '' : 's'}</p>
        </div>
      </div>

      {submissions.length === 0 ? (
        <div className="empty-state">
          <i className="fas fa-inbox"></i>
          <h3>No submissions yet</h3>
          <p>Share this form's public link to start collecting responses.</p>
        </div>
      ) : (
        <div className="monitoring-list">
          {submissions.map((submission) => (
            <div key={submission.id} className="monitoring-card" style={{ flexDirection: 'column', alignItems: 'stretch' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.75rem' }}>
                <span style={{ color: '#6b7280', fontSize: '0.8rem' }}>
                  <i className="fas fa-calendar"></i> {new Date(submission.createdAt).toLocaleString()}
                </span>
                <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  {submission.findingId && (
                    <span className="status-badge status-verified"><i className="fas fa-check"></i> Promoted</span>
                  )}
                  <span className={`status-badge status-${submission.status === 'reviewed' ? 'verified' : 'pending'}`}>
                    {submission.status === 'reviewed' ? 'Reviewed' : 'New'}
                  </span>
                </div>
              </div>

              <div className="parameter-table" style={{ marginBottom: '0.75rem' }}>
                {(form.questions || []).map((q) => (
                  <div key={q.id} style={{ padding: '0.4rem 0', borderBottom: '1px solid #f3f4f6' }}>
                    <div style={{ fontSize: '0.75rem', color: '#6b7280', fontWeight: 600 }}>{q.label}</div>
                    <div style={{ fontSize: '0.9rem', color: '#111827' }}>
                      {Array.isArray(submission.answers[q.id])
                        ? submission.answers[q.id].join(', ') || '—'
                        : submission.answers[q.id] || '—'}
                    </div>
                  </div>
                ))}
              </div>

              <div className="form-group full-width" style={{ marginBottom: '0.5rem' }}>
                <label>Internal Review Notes</label>
                <textarea
                  rows="2"
                  value={notesBySubmission[submission.id] || ''}
                  onChange={(e) => setNotesBySubmission((prev) => ({ ...prev, [submission.id]: e.target.value }))}
                  placeholder="Not visible to the submitter"
                />
              </div>

              <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'flex-end' }}>
                <button
                  className="btn-secondary-outline"
                  disabled={savingId === submission.id}
                  onClick={() => handleSaveNotes(submission)}
                >
                  <i className="fas fa-check"></i> Save & Mark Reviewed
                </button>
                {!submission.findingId && (
                  <button
                    className="btn-primary-green"
                    disabled={savingId === submission.id}
                    onClick={() => setPromotingSubmission(submission)}
                  >
                    <i className="fas fa-arrow-up"></i> Promote to Finding
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {promotingSubmission && (
        <PromoteModal
          submission={promotingSubmission}
          needsProject={!promotingSubmission.projectId}
          projects={projects}
          onClose={() => setPromotingSubmission(null)}
          onSave={handlePromote}
          saving={savingId === promotingSubmission.id}
        />
      )}
    </div>
  );
};

export default FormSubmissions;
